<script setup>
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import {
 Chart as ChartJS,
 Title,
 Tooltip,
 Legend,
 Filler,
 LineElement,
 PointElement,
 CategoryScale,
 LinearScale,
} from 'chart.js';
import AppSmartLoader from '../AppSmartLoader.vue';
import { BAR_COLORS, CHART_TOOLTIP_STYLE } from 'dashboard/constants/brandConstants';

ChartJS.register(Title, Tooltip, Legend, Filler, LineElement, PointElement, CategoryScale, LinearScale);

const props = defineProps({
 title: { type: String, default: '' },
 labels: { type: Array, default: () => [] },
 datasets: { type: Array, default: () => [] },
 loading: { type: Boolean, default: false },
 height: { type: Number, default: 240 },
 dualAxis: { type: Boolean, default: false },
});

function hexToRgba(hex, alpha) {
 const r = parseInt(hex.slice(1, 3), 16);
 const g = parseInt(hex.slice(3, 5), 16);
 const b = parseInt(hex.slice(5, 7), 16);
 return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const chartData = computed(() => ({
 labels: props.labels,
 datasets: props.datasets.map((ds, i) => {
 const color = ds.color || (i === 0 ? BAR_COLORS.main : BAR_COLORS.hover);
 const dataset = {
 label: ds.label || '',
 data: ds.data || [],
 borderColor: color,
 backgroundColor: ds.fill ? hexToRgba(color, 0.1) : color,
 tension: 0.3,
 pointRadius: 3,
 pointHoverRadius: 5,
 borderWidth: 2,
 fill: ds.fill || false,
 borderDash: ds.dashed ? [5, 5] : [],
 };
 if (props.dualAxis) {
 dataset.yAxisID = i === 0 ? 'y' : 'y1';
 }
 return dataset;
 }),
}));

const crosshairPlugin = {
 id: 'crosshair',
 afterDraw(chart) {
 if (chart.tooltip?._active?.length) {
 const { ctx } = chart;
 const activePoint = chart.tooltip._active[0];
 const { x } = activePoint.element;
 const topY = chart.scales.y.top;
 const bottomY = chart.scales.y.bottom;
 ctx.save();
 ctx.beginPath();
 ctx.moveTo(x, topY);
 ctx.lineTo(x, bottomY);
 ctx.lineWidth = 1;
 ctx.strokeStyle = 'rgba(148, 163, 184, 0.3)';
 ctx.stroke();
 ctx.restore();
 }
 },
};

const chartOptions = computed(() => {
 const options = {
 responsive: true,
 maintainAspectRatio: false,
 animation: { duration: 0 },
 interaction: {
 mode: 'index',
 intersect: false,
 },
 plugins: {
 legend: { display: props.dualAxis },
 tooltip: {
 ...CHART_TOOLTIP_STYLE,
 mode: 'index',
 intersect: false,
 },
 },
 scales: {
 x: {
 grid: { display: false },
 ticks: { font: { size: 11 }, color: '#94a3b8' },
 },
 y: {
 beginAtZero: true,
 grid: { color: 'rgba(148,163,184,0.1)' },
 ticks: { font: { size: 11 }, color: '#94a3b8' },
 },
 },
 };

 if (props.dualAxis) {
 options.scales.y1 = {
 position: 'right',
 beginAtZero: true,
 grid: { drawOnChartArea: false },
 ticks: { font: { size: 11 }, color: '#94a3b8' },
 };
 }

 return options;
});

const hasData = computed(() =>
 props.datasets.some(ds => ds.data && ds.data.length > 0)
);
</script>

<template>
 <div class="bg-n-solid-1 rounded-xl border border-n-weak p-4">
 <h4 v-if="title" class="text-sm font-semibold text-n-slate-12 mb-3">
 {{ title }}
 </h4>
 <div v-if="loading" class="flex items-center justify-center" :style="{ height: height + 'px' }">
 <AppSmartLoader animation="wave" :size="32" />
 </div>
 <div v-else-if="!hasData" class="flex items-center justify-center text-sm text-n-slate-9" :style="{ height: height + 'px' }">
 Sem dados para o periodo selecionado
 </div>
 <div v-else :style="{ height: height + 'px' }">
 <Line :data="chartData" :options="chartOptions" :plugins="[crosshairPlugin]" />
 </div>
 </div>
</template>
